import React, { useState, useEffect } from "react"
import { Modal, Radio, Input, Button, Flex, Form } from "antd"
import styles from "./Notify.module.css"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber, PhoneInput } from "ui/Input/PhoneInput"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"

export const NotifyModal = ({ visible, onClose, onSend, loading, guestData }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [sendMethod, setSendMethod] = useState("email")

  const isPrimaryGuest = !!guestData?.is_primary_guest

  useEffect(() => {
    if (!visible) return

    const notifyEmail = guestData?.notify_details?.email || ""
    const notifyPhone = guestData?.notify_details?.phone_number || ""

    if (guestData?.share_by === "email") {
      setSendMethod("email")
      form.setFieldsValue({ sendMethod: "email", email: notifyEmail || guestData.shared_email || "" })
    } else if (guestData?.share_by === "text") {
      setSendMethod("text")
      form.setFieldsValue({
        sendMethod: "text",
        phoneNumber: { fullNumber: notifyPhone || guestData.shared_phone || "", countryCode: "US" }
      })
    } else if (isPrimaryGuest) {
      const resolvedEmail = notifyEmail || guestData?.guest_email || guestData?.shared_email || ""
      const resolvedPhone = notifyPhone || guestData?.phone_number || guestData?.shared_phone || ""
      const defaultMethod = resolvedPhone && !resolvedEmail ? "text" : "email"
      setSendMethod(defaultMethod)
      form.setFieldsValue({
        sendMethod: defaultMethod,
        email: resolvedEmail,
        phoneNumber: { fullNumber: resolvedPhone, countryCode: "US" }
      })
    } else {
      const resolvedEmail = notifyEmail || guestData?.shared_email || ""
      const resolvedPhone = notifyPhone || guestData?.shared_phone || ""
      const defaultMethod = resolvedPhone && !resolvedEmail ? "text" : "email"
      setSendMethod(defaultMethod)
      form.setFieldsValue({
        sendMethod: defaultMethod,
        email: resolvedEmail,
        phoneNumber: { fullNumber: resolvedPhone, countryCode: "US" }
      })
    }
  }, [guestData, visible, form])

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (onSend) {
        onSend({ ...values, sendMethod })
      }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setSendMethod("email")
    if (onClose) {
      onClose()
    }
  }

  const handleSendMethodChange = (e) => {
    const newMethod = e.target.value
    setSendMethod(newMethod)
    // Only clear fields if not primary guest (primary guest fields are pre-populated & disabled)
    if (!isPrimaryGuest) {
      form.setFieldsValue({
        phoneNumber: { fullNumber: "", countryCode: "US" },
        email: ""
      })
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      className={styles.shareWaiverModal}
      closeIcon={<span className={styles.closeIcon}>×</span>}
      centered
      maskClosable={false}
    >
      <Form form={form} layout="vertical">
        <div className="mb-xs">
          <h2 className={styles.modalTitle}>{t("digital_waiver_and_contracts.notify_guest")}</h2>
        </div>

        {/* Radio Buttons */}
        <div className="mb-xs">
          <Form.Item name="sendMethod" initialValue={sendMethod}>
            <Radio.Group onChange={handleSendMethodChange} className={styles.radioGroup}>
              <Radio value="text" className={styles.radio}>
                {t("embedded.booking_confirmation.send_by_text")}
              </Radio>
              <Radio value="email" className={styles.radio}>
                {t("embedded.booking_confirmation.send_by_email")}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Phone Number Input */}
        {sendMethod === "text" && (
          <div className="mb-l">
            <Form.Item
              name="phoneNumber"
              label="Phone Number*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      {
                        validator: (_, value) => {
                          if (!value?.fullNumber?.trim()) {
                            return Promise.reject("Please enter phone number")
                          }
                          return validatePhoneNumber(value.fullNumber)
                        }
                      }
                    ]
              }
            >
              <PhoneInput
                initialCountryCode={form.getFieldValue(["phoneNumber", "countryCode"]) || "US"}
                initialValue={form.getFieldValue(["phoneNumber", "fullNumber"]) || ""}
                disabled={isPrimaryGuest}
                onChange={(value) => {
                  if (!isPrimaryGuest) form.setFieldValue("phoneNumber", value)
                }}
              />
            </Form.Item>
          </div>
        )}

        {/* Email Input */}
        {sendMethod === "email" && (
          <div className="mb-xs">
            <LabeledField
              name="email"
              labelKey="Email Address*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      { required: true, message: "Please enter email address" },
                      { type: "email", message: "Please enter a valid email" }
                    ]
              }
            >
              <Input placeholder="Enter email address" className={styles.emailInput} disabled={isPrimaryGuest} />
            </LabeledField>
          </div>
        )}

        {/* Footer */}
        <Flex justify="end" align="center">
          <Button type="primary" loading={loading} disabled={loading} onClick={handleSend}>
            {t("digital_waiver_and_contracts.notify")}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}
